import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import { useGetBridgedEventByIdQuery } from '../../../services/api';
import { getActiveAndAvailablePlaybooks } from '../../../constants/agents/playbookOnboardingData';
import {
  selectSelectedEvent,
  selectSelectedWorkflowKey,
  setSelectedEvent,
  setSelectedWorkflowKey,
} from '../../../redux/slices/events/eventsSlice';
import { getWorkflowTitle } from '../../../constants/copy/productCopy';
import {
  getEventAgentsPath,
  getEventBrandingPath,
  getEventDetailPath,
  getEventLeadsPath,
  getEventWorkflowAgentsPath,
} from './eventFormatters';

export const EVENT_DETAIL_TABS = Object.freeze({
  WORKFLOWS: 'workflows',
  AGENTS: 'agents',
  BRANDING: 'branding',
  LEADS: 'leads',
});

export const EVENT_DETAIL_TAB_ITEMS = [
  { key: EVENT_DETAIL_TABS.WORKFLOWS, label: 'Workflows' },
  { key: EVENT_DETAIL_TABS.AGENTS, label: 'Agents' },
  { key: EVENT_DETAIL_TABS.BRANDING, label: 'Event branding' },
  { key: EVENT_DETAIL_TABS.LEADS, label: 'Leads' },
];

// Matched on the event's own path: agent pages (/agents/overview, /agents/edit) use this too.
const EVENT_AGENTS_TAB_PATH = /^\/events\/[^/]+\/agents(\/|$)/;

export function getActiveEventTabFromPath(pathname) {
  if (EVENT_AGENTS_TAB_PATH.test(pathname)) {
    return EVENT_DETAIL_TABS.AGENTS;
  }
  if (pathname.includes('/branding')) {
    return EVENT_DETAIL_TABS.BRANDING;
  }
  if (pathname.includes('/leads')) {
    return EVENT_DETAIL_TABS.LEADS;
  }
  return EVENT_DETAIL_TABS.WORKFLOWS;
}

function getTabPath(eventId, tabKey) {
  if (tabKey === EVENT_DETAIL_TABS.AGENTS) {
    return getEventAgentsPath(eventId);
  }
  if (tabKey === EVENT_DETAIL_TABS.BRANDING) {
    return getEventBrandingPath(eventId);
  }
  if (tabKey === EVENT_DETAIL_TABS.LEADS) {
    return getEventLeadsPath(eventId);
  }
  return getEventDetailPath(eventId);
}

function getAgentDisplayName({
  isAgentOverview,
  isAgentCreate,
  isAgentEdit,
  agentType,
  campaignTitle,
  aiAgentTitle,
  smartReplyTitle,
  qualificationTitle,
  overviewTitle,
}) {
  if (isAgentOverview && overviewTitle) return overviewTitle;
  if (agentType === 'aiAgent' && aiAgentTitle) return aiAgentTitle;
  if (agentType === 'smartReply' && smartReplyTitle) return smartReplyTitle;
  if (agentType === 'qualification' && qualificationTitle) return qualificationTitle;
  if (campaignTitle) return campaignTitle;
  if (isAgentCreate) return 'New agent';
  if (isAgentEdit) return 'Edit agent';
  if (isAgentOverview) return 'Overview';
  return null;
}

/**
 * The event context of an event page or an agent page. An agent page passes the agent's own
 * event (and its workflow) once the agent is in: the trail follows the agent, not the event
 * last picked elsewhere, and that event becomes the selected one.
 */
function useEventDetailHeader({ agentEventId = null, agentWorkflowKey = null } = {}) {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const params = useParams();
  const selectedEvent = useSelector(selectSelectedEvent);
  const selectedWorkflowKey = useSelector(selectSelectedWorkflowKey);
  const subscribedPlaybooks = useSelector(state => state.app.subscribedPlaybooks);
  const agentType = useSelector(state => state.agent.etc.agentType);
  const campaignTitle = useSelector(state => state.agent.data?.title);
  const aiAgentTitle = useSelector(state => state.agent.aiAgent?.title);
  const smartReplyTitle = useSelector(state => state.agent.smartReply?.title);
  const qualificationTitle = useSelector(state => state.agent.qualificationAgent?.title);
  const overviewTitle = useSelector(state => state.agentOverview.data?.summary?.title);

  const eventId = params.eventId || agentEventId || selectedEvent?.id || null;
  const workflowKey =
    params.workflowKey || (agentEventId && agentWorkflowKey) || selectedWorkflowKey || null;
  const isAgentOverview = pathname.startsWith('/agents/overview');
  const isAgentCreate = pathname.startsWith('/agents/create');
  const isAgentEdit = pathname.startsWith('/agents/edit');
  const isAgentPage = isAgentOverview || isAgentCreate || isAgentEdit;
  const isWorkflowAgentsPage = Boolean(eventId && workflowKey) && pathname.includes('/workflows/');

  const { data: eventResponse, isLoading: isEventLoading } = useGetBridgedEventByIdQuery(eventId, {
    skip: !eventId,
  });
  const event = eventResponse?.success ? eventResponse.data : null;

  const eventName =
    event?.name || (selectedEvent?.id === eventId ? selectedEvent?.name : null) || 'Event';

  const { playbookDataByKey, activePlaybooksData } = useMemo(
    () => getActiveAndAvailablePlaybooks(subscribedPlaybooks),
    [subscribedPlaybooks]
  );

  const workflow = workflowKey ? playbookDataByKey[workflowKey] || null : null;
  const subscribedWorkflow = workflowKey
    ? (activePlaybooksData || []).find(
        item => item.key === workflowKey || item.playbookType === workflowKey
      )
    : null;

  const workflowTitle = workflowKey
    ? getWorkflowTitle(workflow?.title || subscribedWorkflow?.title || workflowKey)
    : null;

  const workflowBreadcrumbTitle = workflowKey
    ? workflow?.title || subscribedWorkflow?.title || workflowKey
    : null;

  const agentName = getAgentDisplayName({
    isAgentOverview,
    isAgentCreate,
    isAgentEdit,
    agentType,
    campaignTitle,
    aiAgentTitle,
    smartReplyTitle,
    qualificationTitle,
    overviewTitle,
  });

  useEffect(() => {
    if (event?.id) {
      dispatch(setSelectedEvent(event));
    }
  }, [dispatch, event]);

  useEffect(() => {
    if (params.workflowKey) {
      dispatch(setSelectedWorkflowKey(params.workflowKey));
    } else if (agentEventId && agentWorkflowKey) {
      dispatch(setSelectedWorkflowKey(agentWorkflowKey));
    }
  }, [dispatch, params.workflowKey, agentEventId, agentWorkflowKey]);

  const activeTab = getActiveEventTabFromPath(pathname);
  const activeTabLabel =
    EVENT_DETAIL_TAB_ITEMS.find(item => item.key === activeTab)?.label || 'Workflows';
  const tabPath = eventId ? getTabPath(eventId, activeTab) : '/events';
  const workflowPath =
    eventId && workflowKey ? getEventWorkflowAgentsPath(eventId, workflowKey) : null;

  const breadcrumbItems = useMemo(() => {
    if (!eventId) return [];

    const items = [{ label: 'Events', path: '/events' }];
    const isOnTabRoot = !isWorkflowAgentsPage && !isAgentPage;

    items.push({
      label: activeTabLabel,
      path: isOnTabRoot ? null : tabPath,
    });

    if ((isWorkflowAgentsPage || isAgentPage) && workflowBreadcrumbTitle) {
      items.push({
        label: workflowBreadcrumbTitle,
        path: isAgentPage ? workflowPath : null,
      });
    }

    if (isAgentPage && agentName) {
      items.push({ label: agentName, path: null });
    }

    return items;
  }, [
    eventId,
    isWorkflowAgentsPage,
    isAgentPage,
    activeTabLabel,
    tabPath,
    workflowBreadcrumbTitle,
    workflowPath,
    agentName,
  ]);

  let backLabel = 'Events';
  let backPath = '/events';
  let currentLabel = eventName;

  if (isAgentPage && workflowBreadcrumbTitle) {
    backLabel = workflowBreadcrumbTitle;
    backPath = workflowPath || tabPath;
    currentLabel = agentName || 'Agent';
  } else if (isWorkflowAgentsPage && workflowBreadcrumbTitle) {
    backLabel = activeTabLabel;
    backPath = tabPath;
    currentLabel = workflowBreadcrumbTitle;
  }

  return {
    eventId,
    event,
    isEventLoading,
    eventName,
    workflow,
    tabPath,
    workflowKey,
    workflowTitle,
    workflowBreadcrumbTitle,
    activeTab,
    activeTabLabel,
    isAgentPage,
    isWorkflowAgentsPage,
    agentName,
    backLabel,
    backPath,
    currentLabel,
    breadcrumbItems,
  };
}

export default useEventDetailHeader;
